"use client";
import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  Divider,
  Card,
  CardContent,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Badge,
  useTheme,
} from "@mui/material";
import {
  BatteryStd,
  Speed,
  AccessTime,
  Groups,
  Search,
  LocationOn,
} from "@mui/icons-material";

const StaffList = ({ staffList, selectedStaffId, onSelect }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [searchQuery, setSearchQuery] = useState("");

  // Text tones that stay readable on both the light and dark surfaces.
  const strong = isDark ? "#E2E8F0" : "#1E293B";
  const muted = isDark ? "#94A3B8" : "#64748B";
  const soft = isDark ? "#CBD5E1" : "#475569";
  const faint = isDark ? "#64748B" : "#94A3B8";

  const getStatusColor = (status) => {
    const statusColors = {
      Active: "success",
      Idle: "warning",
      "No Data": "default",
      Offline: "error",
      "On Route": "primary",
    };
    return statusColors[status] || "default";
  };

  const getInitials = (name) =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();

  const getAvatarColor = (id) => {
    const colors = [
      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    ];
    return colors[id % colors.length];
  };

  const filteredStaff = useMemo(() => {
    if (!searchQuery) return staffList;
    const query = searchQuery.toLowerCase();
    return staffList.filter(
      (staff) =>
        staff.name.toLowerCase().includes(query) ||
        (staff.status || "").toLowerCase().includes(query) ||
        staff.location?.toLowerCase().includes(query)
    );
  }, [staffList, searchQuery]);

  const activeCount = staffList.filter((s) => s.status === "Active").length;

  return (
    <Card
      sx={{
        width: { xs: "100%", md: "360px" },
        height: "80vh",
        borderRadius: "20px",
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        background: isDark
          ? "linear-gradient(135deg, #1C212B 0%, #12161F 100%)"
          : "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        sx={{
          p: 0,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            p: 2,
            pb: 1,
            borderRadius: "10px",
            background: "linear-gradient(135deg, #56C662 0%, #3d9446ff 100%)",
            flexShrink: 0,
          }}
        >
          <Typography variant="h5" fontWeight="bold" color="white" gutterBottom>
            Field Team
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Badge
              color="success"
              variant="dot"
              sx={{
                "& .MuiBadge-badge": {
                  right: -3,
                  top: -3,
                  backgroundColor: "#4CAF50",
                },
              }}
            >
              <Typography variant="body2" color="white" fontWeight="500">
                {staffList.length} team members
              </Typography>
            </Badge>
            <Typography variant="body2" color="rgba(255,255,255,0.8)">
              • {activeCount} active
            </Typography>
          </Box>
        </Box>

        {/* Search Section */}
        <Box
          sx={{
            pt: 1,
            pb: 2,
            flexShrink: 0,
          }}
        >
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search by name or status..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Divider sx={{ opacity: 0.6, flexShrink: 0 }} />

        {/* Staff List - Scrollable Area */}
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack
            spacing={1.5}
            sx={{
              pt: 2,
              height: "100%",
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "#cbd5e1 transparent",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
                borderRadius: "10px",
                margin: "4px 0",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#cbd5e1",
                borderRadius: "10px",
                border: "2px solid transparent",
                backgroundClip: "content-box",
                minHeight: "40px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#94a3b8",
              },
              "&::-webkit-scrollbar-thumb:active": {
                backgroundColor: "#64748b",
              },
            }}
          >
            {/* All Agents Card */}
            <Card
              onClick={() => onSelect("all")}
              elevation={0}
              sx={{
                cursor: "pointer",
                p: 1,
                borderRadius: "14px",
                background: isDark
                  ? selectedStaffId === "all"
                    ? "linear-gradient(135deg, #16301F 0%, #14261B 100%)"
                    : "linear-gradient(135deg, #1C212B 0%, #12161F 100%)"
                  : selectedStaffId === "all"
                    ? "linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)"
                    : "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
                boxShadow:
                  selectedStaffId === "all"
                    ? "0 6px 20px rgba(76, 175, 80, 0.15)"
                    : "0 2px 8px rgba(0,0,0,0.04)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                  borderColor:
                    selectedStaffId === "all" ? "#4CAF50" : "#cbd5e1",
                },
                position: "relative",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {selectedStaffId === "all" && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: 4,
                    height: "100%",
                    background: "#4CAF50",
                    borderRadius: "4px 0 0 4px",
                  }}
                />
              )}
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    background:
                      "linear-gradient(135deg, #43e97b 0%, #6ff938ff 100%)",
                    boxShadow: "0 4px 12px rgba(66, 224, 119, 0.3)",
                  }}
                >
                  <Groups fontSize="small" sx={{ color: "white" }} />
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight="700" noWrap>
                    All Agents
                  </Typography>
                  <Typography variant="body2" color={muted} noWrap>
                    View all live agents on map
                  </Typography>
                </Box>
              </Stack>
            </Card>

            {/* Individual Staff Cards */}
            {filteredStaff.map((staff) => (
              <Card
                key={staff.id}
                onClick={() => onSelect(staff.id)}
                elevation={0}
                sx={{
                  cursor: "pointer",
                  p: 1,
                  borderRadius: "14px",
                  background: isDark
                    ? selectedStaffId === staff.id
                      ? "linear-gradient(135deg, #14243A 0%, #131E2E 100%)"
                      : "linear-gradient(135deg, #1C212B 0%, #12161F 100%)"
                    : selectedStaffId === staff.id
                      ? "linear-gradient(135deg, #E3F2FD 0%, #F3F8FF 100%)"
                      : "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
                  // border:
                  //   selectedStaffId === staff.id
                  //     ? "2px solid #2196F3"
                  //     : "1px solid #e2e8f0",
                  boxShadow:
                    selectedStaffId === staff.id
                      ? "0 6px 20px rgba(33, 150, 243, 0.15)"
                      : "0 2px 8px rgba(0,0,0,0.04)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                    borderColor:
                      selectedStaffId === staff.id ? "#56C662" : "#cbd5e1",
                  },
                  position: "relative",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                {selectedStaffId === staff.id && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: 4,
                      height: "100%",
                      background: "#4CAF50",
                      borderRadius: "4px 0 0 4px",
                    }}
                  />
                )}

                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Avatar
                    src={staff.agentImg || undefined}
                    alt={staff.name}
                    sx={{
                      width: 44,
                      height: 44,
                      background: staff.agentImg
                        ? "transparent"
                        : getAvatarColor(staff.id),
                      fontWeight: "bold",
                      fontSize: "0.95rem",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      flexShrink: 0,
                    }}
                  >
                    {!staff.agentImg && getInitials(staff.name)}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Name and Status Row */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        mb: 1,
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight="700"
                        color={strong}
                        noWrap
                        sx={{ flex: 1 }}
                      >
                        {staff.name}
                      </Typography>
                      <Chip
                        label={staff.status}
                        size="small"
                        color={getStatusColor(staff.status)}
                        variant={
                          staff.status === "Active" ? "filled" : "outlined"
                        }
                        sx={{
                          fontWeight: "600",
                          fontSize: "0.7rem",
                          minWidth: 60,
                          flexShrink: 0,
                        }}
                      />
                    </Box>

                    {/* Role and Location */}
                    <Typography
                      variant="body2"
                      color={muted}
                      noWrap
                      sx={{ mb: 1 }}
                    >
                      {staff.liveLocation
                        ? `${staff.liveLocation.lat.toFixed(4)}, ${staff.liveLocation.lng.toFixed(4)}`
                        : "No location yet"}
                    </Typography>

                    {staff.location && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mb: 1.5,
                        }}
                      >
                        <LocationOn
                          sx={{ fontSize: 14, color: "#94a3b8", flexShrink: 0 }}
                        />
                        <Typography
                          variant="caption"
                          color={muted}
                          noWrap
                          sx={{ flex: 1 }}
                        >
                          {staff.location}
                        </Typography>
                      </Box>
                    )}

                    {/* Stats Row */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <AccessTime
                          sx={{ fontSize: 14, color: "#94a3b8", flexShrink: 0 }}
                        />
                        <Typography
                          variant="caption"
                          fontWeight="600"
                          color={soft}
                        >
                          {staff.lastUpdate}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Speed
                          sx={{ fontSize: 14, color: "#94a3b8", flexShrink: 0 }}
                        />
                        <Typography
                          variant="caption"
                          fontWeight="600"
                          color={soft}
                        >
                          {staff.speed}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <BatteryStd
                          sx={{ fontSize: 14, color: "#94a3b8", flexShrink: 0 }}
                        />
                        <Typography
                          variant="caption"
                          fontWeight="600"
                          color={soft}
                        >
                          {staff.battery}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              </Card>
            ))}

            {/* Empty State */}
            {filteredStaff.length === 0 && (
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Search sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
                <Typography variant="body1"                          color={muted} fontWeight="500">
                  No agents found
                </Typography>
                <Typography variant="body2" color={faint} sx={{ mt: 0.5 }}>
                  Try adjusting your search terms
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default StaffList;
